//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import moment from 'moment-timezone';

import {
  AudienceDetails,
  FulfillmentDetails,
  MemberTasks,
  Platform,
  SamplingCampaignRequest,
  Social,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { PurchaseOption } from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { SamplingCampaignRequestSectionName } from '@campaigns/constants/sampling/SamplingCampaignRequestSectionConstants';
import {
  hasMissingDimensions,
  isMissingDetails,
} from '@campaigns/utils/sampling/SamplingProductUtils';
import { isDefined } from '@utils/filterPredicates';
import { pluralize } from '@utils/stringUtils';

const platformMapping: Record<Platform, string> = {
  instagramPost: 'Instagram Post',
  instagramStory: 'Instagram Story',
  tiktok: 'TikTok',
};

export type ValidationErrorMap = Map<
  SamplingCampaignRequestSectionName,
  string[]
>;

export const validateProducts = (
  products: BVSamplingProduct[] | undefined,
): string[] => {
  if (!products?.length) {
    return ['Products are required.'];
  }

  const errors: string[] = [];

  const missingDetailsCount = products.reduce((count, product) => {
    if (isMissingDetails(product)) {
      return count + 1;
    }
    return count;
  }, 0);

  const productText = pluralize(missingDetailsCount, 'product');

  if (missingDetailsCount > 0) {
    errors.push(`${productText} missing required details.`);
  }

  return errors;
};

export const validateAudience = (
  audienceDetails: AudienceDetails | undefined,
): string[] => {
  if (!audienceDetails) {
    return ['Audience details are required.'];
  }

  const errors: string[] = [];
  if (!audienceDetails.gender || audienceDetails.gender.length === 0) {
    errors.push('Gender is required.');
  }
  if (!audienceDetails.age) {
    errors.push('Age range is required.');
  }

  return errors;
};

export const validateTasks = (
  memberTasks: MemberTasks | undefined,
): string[] => {
  const errors: string[] = [];

  if (!memberTasks || (!memberTasks.postOnSocial && !memberTasks.writeReview)) {
    errors.push(
      'At least one task must be selected: either a social task or a review task',
    );
    return errors;
  }

  // Check if at least one task is selected
  const hasSocials = !!memberTasks.socials?.length;
  const hasValidReviewTask = memberTasks.writeReview;

  if (!hasSocials && !hasValidReviewTask) {
    errors.push(
      'At least one task must be selected: either a social task or a review task',
    );
  }

  // Validate each social task for handle and hashtag
  memberTasks.socials?.forEach((social: Social) => {
    if (!social.handle || !social.hashtag) {
      const displayName = platformMapping[social.platform];

      errors.push(
        `${displayName} task is missing required details (hashtags and handles).`,
      );
    }
  });

  return errors;
};

export const validatePackaging = (
  fulfillmentOptions: FulfillmentDetails | undefined,
  products: BVSamplingProduct[],
): string[] => {
  const errors: string[] = [];

  if (fulfillmentOptions?.fulfillmentType === 'deals') {
    return [];
  }

  if (!fulfillmentOptions) {
    return ['Packaging options are required.'];
  }

  const isBazaarvoiceShipped =
    fulfillmentOptions.whoShipsProduct === 'bazaarvoice';

  // Box type is only required if bazaarVoice is shipping the box
  if (isBazaarvoiceShipped && !fulfillmentOptions.boxType) {
    errors.push('Packaging type is required.');
  }

  if (fulfillmentOptions.boxType === 'branded') {
    const missingDetailsCount = products.reduce((count, product) => {
      if (hasMissingDimensions(product)) {
        return count + 1;
      }
      return count;
    }, 0);

    if (missingDetailsCount > 0) {
      const productText = pluralize(missingDetailsCount, 'product');
      errors.push(`${productText} missing required dimension details.`);
    }
  }

  if (isBazaarvoiceShipped) {
    const unit = fulfillmentOptions.packageWeight?.unit;
    if (!isDefined(unit)) {
      errors.push('Package weight type is required.');
    } else if (unit === 'metric') {
      const kg = fulfillmentOptions.packageWeight?.kg;
      if (!isDefined(kg)) {
        errors.push('Package weight in kg is required.');
      } else if (kg === 0) {
        errors.push('Package weight must be greater than 0.');
      }
    } else {
      const lb = fulfillmentOptions.packageWeight?.lb;
      const oz = fulfillmentOptions.packageWeight?.oz;

      if (!isDefined(lb) && !isDefined(oz)) {
        errors.push('Package weight in lb or oz is required.');
      } else if ((!isDefined(lb) || lb === 0) && (!isDefined(oz) || oz === 0)) {
        errors.push('Package weight must be greater than 0.');
      }
    }

    if (!isDefined(fulfillmentOptions.participantCount)) {
      errors.push('Participant count is required.');
    }
  }

  return errors;
};

export const validateShippedFulfillment = (
  fulfillmentOptions: FulfillmentDetails,
): string[] => {
  const errors: string[] = [];

  if (!fulfillmentOptions.sendTo) {
    errors.push('Warehouse notification is required.');
  }
  if (!fulfillmentOptions.emails) {
    errors.push('Emails are required.');
  }
  if (!fulfillmentOptions.riskManagement) {
    errors.push('Policy is required.');
  }

  return errors;
};

export const validateDealsFulfillmentClientCodeSupplier = (
  fulfillmentOptions: FulfillmentDetails,
): string[] => {
  const errors: string[] = [];

  if (!fulfillmentOptions.codeType) {
    errors.push('Code type is required.');
  }
  if (!fulfillmentOptions.codeScope) {
    errors.push('Code scope is required.');
  }

  return errors;
};

export const validateDealsFulfillment = (
  fulfillmentOptions: FulfillmentDetails,
): string[] => {
  const errors: string[] = [];

  if (
    !fulfillmentOptions.redemptionType ||
    fulfillmentOptions.redemptionType.length === 0
  ) {
    errors.push('Redemption type is required.');
  }

  if (!fulfillmentOptions.codeValue) {
    errors.push('Code value is required.');
  }

  if (
    !fulfillmentOptions.retailers ||
    fulfillmentOptions.retailers.length === 0
  ) {
    errors.push('Redemption location is required.');
  }

  if (fulfillmentOptions.codeSupplier === 'client') {
    errors.push(
      ...validateDealsFulfillmentClientCodeSupplier(fulfillmentOptions),
    );
  }

  return errors;
};

export const validateFulfillment = (
  fulfillmentOptions: FulfillmentDetails | undefined,
): string[] => {
  if (!fulfillmentOptions) {
    return ['Fulfillment options are required.'];
  }

  switch (fulfillmentOptions.fulfillmentType) {
    case 'shipped':
      return validateShippedFulfillment(fulfillmentOptions);
    case 'deals':
      return validateDealsFulfillment(fulfillmentOptions);
    default:
      return ['Invalid fulfillment type.'];
  }
};

const isPurchaseOptionExpired = (option: PurchaseOption): boolean => {
  return moment(option.expiryDate).isBefore(moment());
};

export const validatePurchaseOptions = (
  purchaseOptions: PurchaseOption[] | undefined,
): string[] => {
  if (!purchaseOptions) {
    return [];
  }

  const errors: string[] = [];

  const expiredCount = purchaseOptions.reduce((count, option) => {
    if (isPurchaseOptionExpired(option)) {
      return count + 1;
    }
    return count;
  }, 0);

  if (expiredCount > 0) {
    const optionText = pluralize(expiredCount, 'purchase option');
    errors.push(`${optionText} have expired dates.`);
  }

  return errors;
};

export const hasProductsWithMissingDetails = (
  campaign: SamplingCampaignRequest,
): ValidationErrorMap => {
  const errorMap: ValidationErrorMap = new Map();

  if (campaign.products && campaign.products.length > 0) {
    const productErrors = validateProducts(campaign.products).filter((error) =>
      error.includes('missing required details'),
    );

    if (productErrors.length > 0) {
      errorMap.set(SamplingCampaignRequestSectionName.PRODUCTS, productErrors);
    }
  }

  return errorMap;
};

export const validateForRequest = (
  campaign: SamplingCampaignRequest,
): ValidationErrorMap => {
  const errorMap: ValidationErrorMap = new Map();

  const productErrors = validateProducts(campaign.products);
  if (productErrors.length > 0) {
    errorMap.set(SamplingCampaignRequestSectionName.PRODUCTS, productErrors);
  }

  const audienceErrors = validateAudience(campaign.audienceDetails);
  if (audienceErrors.length > 0) {
    errorMap.set(SamplingCampaignRequestSectionName.AUDIENCE, audienceErrors);
  }

  const taskErrors = validateTasks(campaign.memberTasks);
  if (taskErrors.length > 0) {
    errorMap.set(SamplingCampaignRequestSectionName.TASKS, taskErrors);
  }

  const packagingErrors = validatePackaging(
    campaign.fulfillmentOptions,
    campaign.products ?? [],
  );
  if (packagingErrors.length > 0) {
    errorMap.set(SamplingCampaignRequestSectionName.PACKAGING, packagingErrors);
  }

  const fulfillmentErrors = validateFulfillment(campaign.fulfillmentOptions);
  if (fulfillmentErrors.length > 0) {
    errorMap.set(
      SamplingCampaignRequestSectionName.FULFILLMENT,
      fulfillmentErrors,
    );
  }

  const purchaseOptionErrors = validatePurchaseOptions(campaign.purchaseOption);
  if (purchaseOptionErrors.length > 0) {
    errorMap.set(
      SamplingCampaignRequestSectionName.PURCHASE_OPTIONS,
      purchaseOptionErrors,
    );
  }

  return errorMap;
};
